import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { auth, missingFirebaseConfigKeys } from '../lib/firebase';
import { createUser, getUser, updateUser, initializeUserProgress, getAllModules } from '../lib/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Password validation helper
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*...)');
  }
  
  // Check for common weak passwords
  const commonPasswords = ['password', '12345678', 'qwerty123', 'admin123', 'letmein', 'welcome', 'monkey123'];
  if (commonPasswords.some(p => password.toLowerCase().includes(p))) {
    errors.push('Password is too common. Please choose a stronger password.');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Email validation helper
export const validateEmail = (email) => {
  const errors = [];
  
  // Basic format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    errors.push('Please enter a valid email address');
    return { isValid: false, errors };
  }
  
  // Check for disposable/temporary email domains
  const disposableDomains = [
    'tempmail.com', 'throwaway.com', 'guerrillamail.com', 'mailinator.com',
    '10minutemail.com', 'temp-mail.org', 'fakeinbox.com', 'trashmail.com',
    'yopmail.com', 'maildrop.cc', 'sharklasers.com', 'getairmail.com',
    'dispostable.com', 'mailnesia.com', 'getnada.com'
  ];
  
  const domain = email.split('@')[1]?.toLowerCase();
  if (disposableDomains.includes(domain)) {
    errors.push('Disposable email addresses are not allowed. Please use a permanent email.');
  }
  
  // Check for obviously fake patterns
  const localPart = email.split('@')[0];
  if (/^(test|fake|temp|demo|sample|example|asdf|qwerty)/.test(localPart.toLowerCase())) {
    // Just a warning, not blocking
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get password strength
export const getPasswordStrength = (password) => {
  let score = 0;
  
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
  
  if (score <= 2) return { level: 'weak', color: '#EF4444', width: '33%' };
  if (score <= 4) return { level: 'medium', color: '#F59E0B', width: '66%' };
  return { level: 'strong', color: '#10B981', width: '100%' };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const firebaseConfigError = missingFirebaseConfigKeys.length
    ? `Firebase is not configured. Missing: ${missingFirebaseConfigKeys.join(', ')}.`
    : null;

  useEffect(() => {
    if (!auth) {
      setUser(null);
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Fetch user data from Firestore
          const userData = await getUser(firebaseUser.uid);
          if (userData) {
            // Check if user is disabled
            if (userData.status === 'disabled') {
              await signOut(auth);
              setUser(null);
              setLoading(false);
              return;
            }
            
            // Update emailVerified status in Firestore if changed
            if (firebaseUser.emailVerified && !userData.emailVerified) {
              await updateUser(firebaseUser.uid, { emailVerified: true });
            }
            
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              emailVerified: firebaseUser.emailVerified,
              name: userData.name || firebaseUser.displayName,
              role: userData.role || 'student',
              status: userData.status || 'active',
              ...userData
            });
          } else {
            // User exists in Firebase Auth but not in Firestore (edge case)
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              emailVerified: firebaseUser.emailVerified,
              name: firebaseUser.displayName || 'User',
              role: 'student',
              status: 'active'
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    if (!auth) {
      throw new Error(firebaseConfigError || 'Firebase authentication is unavailable.');
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userData = await getUser(userCredential.user.uid);
      
      // Check if user is disabled
      if (userData?.status === 'disabled') {
        await signOut(auth);
        throw new Error('Your account has been disabled. Please contact an administrator.');
      }
      
      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        // Allow login but return verification status
        return {
          id: userCredential.user.uid,
          email: userCredential.user.email,
          emailVerified: false,
          needsVerification: true,
          ...userData
        };
      }
      
      return {
        id: userCredential.user.uid,
        email: userCredential.user.email,
        emailVerified: true,
        ...userData
      };
    } catch (error) {
      console.error('Login error:', error);
      if (error.message.includes('disabled')) {
        throw error;
      }
      throw new Error(getFirebaseErrorMessage(error.code));
    }
  };

  const signup = async (email, password, name, cohortId = 'default') => {
    if (!auth) {
      throw new Error(firebaseConfigError || 'Firebase authentication is unavailable.');
    }

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      throw new Error(emailValidation.errors[0]);
    }
    
    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      throw new Error(passwordValidation.errors[0]);
    }
    
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name first
      await updateProfile(userCredential.user, { displayName: name });
      
      // Try to send email verification (may fail if domain not authorized)
      let verificationEmailSent = false;
      try {
        await sendEmailVerification(userCredential.user, {
          url: window.location.origin + '/dashboard',
          handleCodeInApp: false
        });
        verificationEmailSent = true;
      } catch (verificationError) {
        console.warn('Email verification could not be sent:', verificationError.message);
        // Continue with signup - verification can be done later
      }
      
      // Create user document in Firestore
      await createUser(userCredential.user.uid, {
        email,
        name,
        role: 'student',
        status: 'active',
        emailVerified: false,
        cohortId
      });

      // Initialize user progress with first module unlocked
      try {
        const modules = await getAllModules();
        if (modules.length > 0) {
          const firstModule = modules.sort((a, b) => a.order - b.order)[0];
          await initializeUserProgress(userCredential.user.uid, firstModule.id);
        }
      } catch (progressError) {
        console.error('Error initializing progress:', progressError);
      }

      return {
        id: userCredential.user.uid,
        email,
        name,
        role: 'student',
        status: 'active',
        emailVerified: false,
        verificationEmailSent
      };
    } catch (error) {
      console.error('Signup error:', error);
      if (error.message.includes('Password') || error.message.includes('email')) {
        throw error;
      }
      throw new Error(getFirebaseErrorMessage(error.code));
    }
  };

  const resendVerificationEmail = async () => {
    if (!auth) {
      throw new Error(firebaseConfigError || 'Firebase authentication is unavailable.');
    }

    const currentUser = auth.currentUser;
    if (currentUser && !currentUser.emailVerified) {
      await sendEmailVerification(currentUser, {
        url: window.location.origin + '/dashboard',
        handleCodeInApp: false
      });
      return true;
    }
    return false;
  };

  const logout = async () => {
    if (!auth) {
      setUser(null);
      return;
    }

    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, resendVerificationEmail, firebaseConfigError }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper function to convert Firebase error codes to user-friendly messages
const getFirebaseErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please login instead.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 8 characters with uppercase, lowercase, numbers, and special characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    default:
      return 'An error occurred. Please try again.';
  }
};
